import { useState, useMemo, useCallback, useRef, useEffect, type MouseEvent as ReactMouseEvent } from "react";

import { PageHeader } from "@/components/page-header";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUploadDialog } from "@/components/ui/file-upload-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload,
  Trash2,
  Pencil,
  X,
  FolderOpen,
  Loader2,
  MoreVertical,
  Download,
  Eye,
} from "lucide-react";
import { format } from "date-fns";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { InsightsPageWrapper } from "@/components/insights/insights-layout";
import { FileViewerDialog, getFileTypeIcon, isPreviewable } from "@/components/ui/file-viewer-dialog";
import type { ViewerFile } from "@/components/ui/file-viewer-dialog";
import { useInsightsContext } from "@/contexts/insights-context";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface FileRecord {
  id: string;
  entityType: string;
  entityId: string | null;
  filename: string;
  displayName: string | null;
  description: string | null;
  fileSize: number;
  fileSizeFormatted: string;
  mimeType: string;
  category: string;
  thumbnailUrl: string | null;
  createdBy: string | null;
  createdAt: string;
  uploadedBy: string | null;
  animals: Array<{ animalId: string; animalName: string | null }>;
  locations: string[];
}

interface FilesResponse {
  files: FileRecord[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AnimalSearchResult {
  animalId: string;
  name: string | null;
  species: string | null;
  location: string | null;
}

export default function FilesPage() {
  usePageTitle("Files");
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.isAdmin ?? false;
  const { selectedShelter, setSelectedShelter, isSystemView } = useInsightsContext();
  const { locations: activeLocations } = useActiveLocations();

  const [hoverThumb, setHoverThumb] = useState<{ url: string; x: number; y: number } | null>(null);

  const handleThumbEnter = useCallback((e: ReactMouseEvent, url: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoverThumb({ url, x: rect.right + 8, y: rect.top });
  }, []);

  const handleThumbLeave = useCallback(() => {
    setHoverThumb(null);
  }, []);

  const locationLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of activeLocations) {
      map.set(loc.code, loc.label);
    }
    return map;
  }, [activeLocations]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFiles, setViewerFiles] = useState<ViewerFile[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileRecord | null>(null);

  const [uploadAnimalIds, setUploadAnimalIds] = useState<string[]>([]);
  const [animalSearchQuery, setAnimalSearchQuery] = useState("");

  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAnimalIds, setEditAnimalIds] = useState<string[]>([]);
  const [editAnimalSearchQuery, setEditAnimalSearchQuery] = useState("");

  const searchTimeout = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchTimeout[0]) clearTimeout(searchTimeout[0]);
    const timeout = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    searchTimeout[0] = timeout;
  }, []);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (categoryFilter !== "all") params.set("category", categoryFilter);
    if (entityTypeFilter !== "all") params.set("entityType", entityTypeFilter);
    if (!isSystemView) params.set("shelter", selectedShelter);
    return params.toString();
  }, [page, pageSize, debouncedSearch, categoryFilter, entityTypeFilter, selectedShelter, isSystemView]);

  const { data, isLoading } = useQuery<FilesResponse>({
    queryKey: ["/api/files", queryParams],
    queryFn: async () => {
      const response = await fetch(`/api/files?${queryParams}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch files");
      return response.json();
    },
  });

  const { data: animalSearchResults } = useQuery<{ animals: AnimalSearchResult[] }>({
    queryKey: ["/api/tasks/animal-search", animalSearchQuery],
    queryFn: async () => {
      const response = await fetch(`/api/tasks/animal-search?q=${encodeURIComponent(animalSearchQuery)}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to search animals");
      return response.json();
    },
    enabled: animalSearchQuery.length >= 2,
  });

  const { data: editAnimalSearchResults } = useQuery<{ animals: AnimalSearchResult[] }>({
    queryKey: ["/api/tasks/animal-search", editAnimalSearchQuery],
    queryFn: async () => {
      const response = await fetch(`/api/tasks/animal-search?q=${encodeURIComponent(editAnimalSearchQuery)}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to search animals");
      return response.json();
    },
    enabled: editAnimalSearchQuery.length >= 2,
  });

  const { data: recentAnimals } = useQuery<{ animals: AnimalSearchResult[] }>({
    queryKey: ["/api/users/me/recent-animals"],
    queryFn: async () => {
      const response = await fetch("/api/users/me/recent-animals", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch recent animals");
      return response.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, displayName, description }: { file: File; displayName: string; description: string }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (displayName) formData.append("displayName", displayName);
      if (description) formData.append("description", description);
      if (uploadAnimalIds.length > 0) formData.append("animalIds", JSON.stringify(uploadAnimalIds));
      return apiRequest("POST", "/api/files", formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/files"] });
      setUploadDialogOpen(false);
      setUploadAnimalIds([]);
      setAnimalSearchQuery("");
      toast({ title: "File uploaded successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({ fileId, body }: { fileId: string; body: any }) => {
      return apiRequest("PATCH", `/api/files/${fileId}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/files"] });
      setEditDialogOpen(false);
      setSelectedFile(null);
      toast({ title: "File updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      return apiRequest("DELETE", `/api/files/${fileId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/files"] });
      setDeleteDialogOpen(false);
      setSelectedFile(null);
      toast({ title: "File deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });

  function resetUploadForm() {
    setUploadAnimalIds([]);
    setAnimalSearchQuery("");
  }

  function handleEditSubmit() {
    if (!selectedFile) return;
    editMutation.mutate({
      fileId: selectedFile.id,
      body: {
        displayName: editDisplayName.trim() || null,
        description: editDescription.trim() || null,
        animalIds: editAnimalIds,
      },
    });
  }

  function openEditDialog(file: FileRecord) {
    setSelectedFile(file);
    setEditDisplayName(file.displayName || "");
    setEditDescription(file.description || "");
    setEditAnimalIds(file.animals.map(a => a.animalId));
    setEditAnimalSearchQuery("");
    setEditDialogOpen(true);
  }

  function openDeleteDialog(file: FileRecord) {
    setSelectedFile(file);
    setDeleteDialogOpen(true);
  }

  async function handleFileClick(file: FileRecord) {
    if (isPreviewable(file.mimeType)) {
      const previewableFiles = files.filter(f => isPreviewable(f.mimeType));
      const clickedIndex = previewableFiles.findIndex(f => f.id === file.id);

      try {
        const urlResults = await Promise.all(
          previewableFiles.map(f =>
            fetch(`/api/files/${f.id}/url`, { credentials: "include" })
              .then(r => {
                if (!r.ok) return null;
                return r.json();
              })
              .then(urlData => urlData?.url ? ({
                url: urlData.url as string,
                filename: f.displayName || f.filename,
                mimeType: f.mimeType,
                description: f.description,
                animals: f.animals,
                locations: f.locations,
              }) : null)
              .catch(() => null)
          )
        );

        if (!urlResults[clickedIndex]?.url) {
          toast({ title: "Failed to load file preview", variant: "destructive" });
          return;
        }

        const viewerList: ViewerFile[] = [];
        let adjustedIndex = 0;
        for (let i = 0; i < urlResults.length; i++) {
          const result = urlResults[i];
          if (result?.url) {
            if (i === clickedIndex) adjustedIndex = viewerList.length;
            viewerList.push({
              url: result.url,
              filename: result.filename,
              mimeType: result.mimeType,
              description: result.description,
              animals: result.animals,
              locations: result.locations,
            });
          }
        }

        setViewerFiles(viewerList);
        setViewerIndex(adjustedIndex);
        setViewerOpen(true);
      } catch {
        toast({ title: "Failed to load file preview", variant: "destructive" });
      }
    } else {
      fetch(`/api/files/${file.id}/url`, { credentials: "include" })
        .then(r => r.json())
        .then(urlData => {
          if (urlData.url) window.open(urlData.url, "_blank");
        })
        .catch(() => {
          toast({ title: "Failed to get download link", variant: "destructive" });
        });
    }
  }

  function handleDownload(file: FileRecord) {
    fetch(`/api/files/${file.id}/url?download=true`, { credentials: "include" })
      .then(r => {
        if (!r.ok) throw new Error("Failed to get download link");
        return r.json();
      })
      .then(urlData => {
        if (urlData.url) {
          window.open(urlData.url, "_blank");
        }
      })
      .catch(() => {
        toast({ title: "Failed to get download link", variant: "destructive" });
      });
  }

  const hasActiveFilters = categoryFilter !== "all" || entityTypeFilter !== "all" || debouncedSearch.length > 0 || !isSystemView;

  const filterDescription = useMemo(() => {
    const parts: string[] = [];

    if (categoryFilter !== "all") {
      const categoryLabels: Record<string, string> = {
        image: "images",
        video: "videos",
        audio: "audio files",
        document: "documents",
        other: "other files",
      };
      parts.push(categoryLabels[categoryFilter] || categoryFilter);
    } else {
      parts.push("all files");
    }

    if (entityTypeFilter !== "all") {
      const sourceLabels: Record<string, string> = {
        task: "from tasks",
        animal: "from animal profiles",
        meeting: "from meetings",
        direct: "directly uploaded",
      };
      parts.push(sourceLabels[entityTypeFilter] || `from ${entityTypeFilter}`);
    }

    if (!isSystemView) {
      const locLabel = locationLabelMap.get(selectedShelter) || selectedShelter;
      parts.push(`at ${locLabel}`);
    }

    if (debouncedSearch) {
      parts.push(`matching "${debouncedSearch}"`);
    }

    const sentence = parts.join(" ");
    return sentence.charAt(0).toUpperCase() + sentence.slice(1) + ".";
  }, [categoryFilter, entityTypeFilter, debouncedSearch, isSystemView, selectedShelter, locationLabelMap]);

  function canEditFile(file: FileRecord): boolean {
    if (isAdmin) return true;
    return file.createdBy === user?.id;
  }

  const files = data?.files ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const startIndex = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(page * pageSize, total);

  return (
    <InsightsPageWrapper>
      <div className="space-y-6">
        <PageHeader
          breadcrumb={[{ label: "Monitoring" }, { label: "Files" }]}
          title={
            <span className="flex items-center gap-2" data-testid="text-files-title">
              <FolderOpen className="w-6 h-6 text-primary" />
              Files
            </span>
          }
          description="Photos, documents, and attachments uploaded across tasks, animals, and direct uploads."
          actions={
            <Button onClick={() => { resetUploadForm(); setUploadDialogOpen(true); }} data-testid="button-upload-file">
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </Button>
          }
        />

        <Card>
          <CardContent className="p-4">
            <CollapsibleFilterHeader
              collapsed={filterCollapsed}
              onToggle={() => setFilterCollapsed(!filterCollapsed)}
              description={filterDescription}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={() => {
                setCategoryFilter("all");
                setEntityTypeFilter("all");
                setSearchQuery("");
                setDebouncedSearch("");
                setSelectedShelter("system");
              }}
              descriptionTestId="text-files-filter-description"
              toggleTestId="button-toggle-files-filter"
              clearTestId="button-clear-files-filters"
            >
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="w-full sm:w-auto">
                  <Input
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full sm:w-[250px]"
                    data-testid="input-files-search"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={(val) => { setCategoryFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-[150px]" data-testid="select-files-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="image">Images</SelectItem>
                    <SelectItem value="video">Videos</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                    <SelectItem value="document">Documents</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={entityTypeFilter} onValueChange={(val) => { setEntityTypeFilter(val); setPage(1); }}>
                  <SelectTrigger className="w-[150px]" data-testid="select-files-source">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="animal">Animal</SelectItem>
                    <SelectItem value="meeting">Meeting Agendas</SelectItem>
                    <SelectItem value="direct">Direct Upload</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedShelter} onValueChange={(val) => { setSelectedShelter(val as any); setPage(1); }}>
                  <SelectTrigger className="w-[180px]" data-testid="select-files-location">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">All Locations</SelectItem>
                    {activeLocations.map((loc) => (
                      <SelectItem key={loc.code} value={loc.code}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CollapsibleFilterHeader>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12" data-testid="loading-files">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading files...</span>
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="empty-state-files">
                <FolderOpen className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">
                  {debouncedSearch || hasActiveFilters ? "No files match your filters" : "No files yet"}
                </p>
                <p className="text-sm">
                  {debouncedSearch || hasActiveFilters ? "Try adjusting your search or filter criteria" : "Upload files to get started"}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[250px]">Name</TableHead>
                        <TableHead className="w-[100px]">Type</TableHead>
                        <TableHead className="w-[80px]">Size</TableHead>
                        <TableHead className="w-[100px]">Source</TableHead>
                        <TableHead className="w-[120px]">Location</TableHead>
                        <TableHead className="min-w-[120px]">Animals</TableHead>
                        <TableHead className="w-[130px]">Uploaded By</TableHead>
                        <TableHead className="w-[120px]">Date</TableHead>
                        <TableHead className="w-[50px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {files.map((file) => {
                        const IconComponent = getFileTypeIcon(file.mimeType);
                        const displayLabel = file.displayName || file.filename;
                        const sourceLabels: Record<string, string> = {
                          task: "Task",
                          animal: "Animal",
                          meeting: "Meeting",
                          direct: "Direct",
                        };

                        return (
                          <TableRow key={file.id} data-testid={`row-file-${file.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-3 min-w-0">
                                {file.thumbnailUrl ? (
                                  <img
                                    src={file.thumbnailUrl}
                                    alt={displayLabel}
                                    className="h-8 w-8 rounded object-cover shrink-0 cursor-pointer"
                                    data-testid={`img-thumbnail-${file.id}`}
                                    onMouseEnter={(e) => handleThumbEnter(e, file.thumbnailUrl!)}
                                    onMouseLeave={handleThumbLeave}
                                  />
                                ) : (
                                  <IconComponent className="h-5 w-5 text-muted-foreground shrink-0" />
                                )}
                                <button
                                  onClick={() => handleFileClick(file)}
                                  className="text-sm font-medium text-left truncate hover:underline cursor-pointer min-w-0"
                                  data-testid={`link-filename-${file.id}`}
                                >
                                  {displayLabel}
                                </button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs capitalize" data-testid={`badge-category-${file.id}`}>
                                {file.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-size-${file.id}`}>
                              {file.fileSizeFormatted}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground" data-testid={`text-source-${file.id}`}>
                                {sourceLabels[file.entityType] || file.entityType}
                              </span>
                            </TableCell>
                            <TableCell data-testid={`text-location-${file.id}`}>
                              {file.locations.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {file.locations.map((loc) => (
                                    <span key={loc} className="text-sm text-muted-foreground">
                                      {locationLabelMap.get(loc) || loc}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {file.animals.length > 0 ? (
                                  file.animals.map((a) => (
                                    <a key={a.animalId} href={`/animals/${a.animalId}`} target="_blank" rel="noopener noreferrer">
                                      <Badge variant="outline" className="text-xs cursor-pointer" data-testid={`badge-animal-${file.id}-${a.animalId}`}>
                                        {a.animalName || a.animalId}
                                      </Badge>
                                    </a>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-uploader-${file.id}`}>
                              {file.uploadedBy || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-date-${file.id}`}>
                              {format(new Date(file.createdAt), "MMM d, yyyy")}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`button-actions-${file.id}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                    <span className="sr-only">Actions</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isPreviewable(file.mimeType) && (
                                    <DropdownMenuItem
                                      onClick={() => handleFileClick(file)}
                                      data-testid={`button-preview-${file.id}`}
                                    >
                                      <Eye className="h-4 w-4 mr-2" />
                                      Preview
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => handleDownload(file)}
                                    data-testid={`button-download-${file.id}`}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                  {canEditFile(file) && (
                                    <DropdownMenuItem
                                      onClick={() => openEditDialog(file)}
                                      data-testid={`button-edit-${file.id}`}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  {isAdmin && (
                                    <DropdownMenuItem
                                      onClick={() => openDeleteDialog(file)}
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`button-delete-${file.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  total={total}
                  pageSize={pageSize}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                  itemLabel="files"
                  testIdPrefix="files"
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={(open) => { if (!open) { setUploadDialogOpen(false); resetUploadForm(); } else { setUploadDialogOpen(true); } }}
        onUpload={(file, displayName, description) => uploadMutation.mutate({ file, displayName, description })}
        isPending={uploadMutation.isPending}
        dialogDescription="Upload a file to the system. You can optionally tag it to animals."
      >
        <div>
          <Label>Tag Animals (optional)</Label>
          <p className="text-xs text-muted-foreground mb-1">
            Link this file to one or more animals so it appears on their BETI profile. Search by name or ID.
          </p>
          <AnimalTagSelector
            selectedAnimalIds={uploadAnimalIds}
            onSelectedChange={setUploadAnimalIds}
            searchQuery={animalSearchQuery}
            onSearchChange={setAnimalSearchQuery}
            searchResults={animalSearchResults?.animals}
            recentAnimals={recentAnimals?.animals}
            testIdPrefix="upload"
          />
        </div>
      </FileUploadDialog>

      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) { setEditDialogOpen(false); setSelectedFile(null); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit File</DialogTitle>
            <DialogDescription>
              Update the display name, description, or animal tags for this file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Original Filename</Label>
              <p className="text-sm text-muted-foreground mt-1" data-testid="text-edit-original-filename">
                {selectedFile?.filename}
              </p>
            </div>
            <div>
              <Label htmlFor="edit-display-name">Display Name</Label>
              <Input
                id="edit-display-name"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                placeholder="Friendly name for this file"
                data-testid="input-edit-display-name"
                className="mt-1"
                maxLength={150}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description of this file"
                data-testid="input-edit-description"
                className="mt-1 resize-none"
                rows={2}
                maxLength={500}
              />
            </div>
            <div>
              <Label>Animal Tags</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Link this file to one or more animals so it appears on their BETI profile. Search by name or ID.
              </p>
              <AnimalTagSelector
                selectedAnimalIds={editAnimalIds}
                onSelectedChange={setEditAnimalIds}
                searchQuery={editAnimalSearchQuery}
                onSearchChange={setEditAnimalSearchQuery}
                searchResults={editAnimalSearchResults?.animals}
                recentAnimals={recentAnimals?.animals}
                knownAnimals={selectedFile?.animals}
                testIdPrefix="edit"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setSelectedFile(null); }} data-testid="button-cancel-edit">
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={editMutation.isPending}
              data-testid="button-submit-edit"
            >
              {editMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setSelectedFile(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedFile?.displayName || selectedFile?.filename}"? This action will hide the file from all views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedFile && deleteMutation.mutate(selectedFile.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FileViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        files={viewerFiles}
        initialIndex={viewerIndex}
      />

      {hoverThumb && (
        <div
          className="fixed z-[9999] pointer-events-none rounded-md border border-border shadow-lg overflow-hidden"
          style={{ left: hoverThumb.x, top: hoverThumb.y }}
        >
          <img
            src={hoverThumb.url}
            alt="Preview"
            className="w-48 h-48 object-cover"
          />
        </div>
      )}
    </InsightsPageWrapper>
  );
}

interface AnimalTagSelectorProps {
  selectedAnimalIds: string[];
  onSelectedChange: (ids: string[]) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchResults?: AnimalSearchResult[];
  recentAnimals?: AnimalSearchResult[];
  knownAnimals?: Array<{ animalId: string; animalName: string | null }>;
  testIdPrefix: string;
}

function AnimalTagSelector({
  selectedAnimalIds,
  onSelectedChange,
  searchQuery,
  onSearchChange,
  searchResults,
  recentAnimals,
  knownAnimals,
  testIdPrefix,
}: AnimalTagSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [nameCache, setNameCache] = useState<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (knownAnimals) {
      for (const a of knownAnimals) {
        if (a.animalName) map.set(a.animalId, a.animalName);
      }
    }
    return map;
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayAnimals = searchQuery.length >= 2
    ? (searchResults || [])
    : (recentAnimals || []);

  useEffect(() => {
    let changed = false;
    const next = new Map(nameCache);
    for (const a of displayAnimals) {
      if (a.name && !next.has(a.animalId)) {
        next.set(a.animalId, a.name);
        changed = true;
      }
    }
    if (changed) setNameCache(next);
  }, [displayAnimals]);

  const addAnimal = (animalId: string) => {
    if (!selectedAnimalIds.includes(animalId)) {
      onSelectedChange([...selectedAnimalIds, animalId]);
    }
  };

  const removeAnimal = (animalId: string) => {
    onSelectedChange(selectedAnimalIds.filter(id => id !== animalId));
  };

  const selectedSet = new Set(selectedAnimalIds);

  const getAnimalLabel = (animalId: string): string => {
    return nameCache.get(animalId) || animalId;
  };

  const unselectedAnimals = displayAnimals.filter(a => !selectedSet.has(a.animalId));
  const showDropdown = dropdownOpen && (unselectedAnimals.length > 0 || (searchQuery.length >= 2 && (!searchResults || searchResults.length === 0)));

  

  return (
    <div className="space-y-2 mt-1">
      {selectedAnimalIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedAnimalIds.map(id => (
            <Badge key={id} variant="secondary" className="gap-1">
              {getAnimalLabel(id)}
              <button
                onClick={() => removeAnimal(id)}
                className="ml-1"
                data-testid={`button-${testIdPrefix}-remove-animal-${id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative" ref={containerRef}>
        {showDropdown && (
          <div ref={dropdownRef} className="absolute left-0 right-0 bottom-full z-50 mb-1 border rounded-md max-h-32 overflow-y-auto bg-popover shadow-md">
            {unselectedAnimals.length > 0 ? (
              unselectedAnimals.map(a => (
                <button
                  key={a.animalId}
                  onClick={() => { addAnimal(a.animalId); setDropdownOpen(false); onSearchChange(""); }}
                  className="w-full text-left px-2 py-1 text-sm hover-elevate"
                  data-testid={`button-${testIdPrefix}-add-animal-${a.animalId}`}
                >
                  {a.name || a.animalId} ({a.animalId}){a.species && ` - ${a.species}`}{a.location && ` - ${a.location}`}
                </button>
              ))
            ) : (
              <p className="text-xs text-muted-foreground px-2 py-1">No animals found</p>
            )}
          </div>
        )}
        <Input
          placeholder="Search animals..."
          value={searchQuery}
          onChange={(e) => { onSearchChange(e.target.value); setDropdownOpen(true); }}
          onFocus={() => setDropdownOpen(true)}
          data-testid={`input-${testIdPrefix}-animal-search`}
        />
      </div>
    </div>
  );
}
